import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useT } from "@/i18n/helpers";
import { FadeUp } from "@/components/Reveal";
import { SITE_URL } from "@/i18n/seoConfig";
import RegionalMap from "@/components/RegionalMap";
import { preserveEleone } from "@/utils/preserveEleone";
import LeadCaptureForm from "@/components/LeadCaptureForm";

const HERO = "/photos/canchungo-hero.jpg";
const PDF_URL = "/nanda-sa-opportunites-guinee-bissau.pdf";

const Section = ({ children, className = "", testId }) => (
 <section data-testid={testId} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[880px] px-6 md:px-10 py-16 md:py-24">{children}</div>
 </section>
);

const H2 = ({ children }) => (
 <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-nanda-ink mb-8">{children}</h2>
);
const H3 = ({ children }) => (
 <h3 className="font-serif text-xl md:text-2xl text-nanda-ink mt-10 mb-4">{children}</h3>
);
const P = ({ children }) => (
 <p className="text-nanda-inkSoft text-[16px] md:text-[17px] leading-[1.8] mb-5">{children}</p>
);
const UL = ({ items }) => (
 <ul className="space-y-2 mb-6 text-nanda-inkSoft text-[16px] md:text-[17px] leading-[1.75]">
 {items.map((it, i) => (
 <li key={i} className="flex items-baseline gap-3">
 <span className="h-px w-4 bg-nanda-terracotta shrink-0 mt-3" />
 <span>{it}</span>
 </li>
 ))}
 </ul>
);

/* Bloc CTA contextuel inséré dans les sections de l'article. */
const InlineCTA = ({ to, eyebrow, title, description, cta, testId }) => (
 <div
 data-testid={testId}
 className="my-10 border-l-2 border-nanda-terracotta bg-nanda-sand/30 pl-6 pr-4 py-6"
 >
 <div className="label-overline text-nanda-terracotta mb-2">{preserveEleone(eyebrow)}</div>
 <div className="font-serif text-lg md:text-xl text-nanda-ink mb-2 leading-snug">{title}</div>
 <p className="text-nanda-inkSoft text-[15px] leading-[1.7] mb-4">{description}</p>
 <Link
 to={to}
 className="inline-flex items-center gap-2 label-overline text-nanda-ink border-b border-nanda-ink pb-0.5 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-300"
 >
 {preserveEleone(cta)}
 <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </div>
);

/* CTAs contextuels — s'adaptent automatiquement à la langue courante. */
const NandaSaFoodCTA = () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-nanda-sa"
 to="/marques/nanda-sa"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "NANDA SÁ. La cajou de Guinée-Bissau, transformée avec exigence.",
 "NANDA SÁ. Cashew from Guinea-Bissau, processed with rigour.",
 "NANDA SÁ. O caju da Guiné-Bissau, transformado com exigência."
 )}
 description={tt(
 "Notre maison épicerie fine incarne concrètement la stratégie de transformation locale évoquée dans cette page. Torréfaction, aromatisation, conditionnement premium : la valeur reste en Guinée-Bissau.",
 "Our fine-food house is the concrete embodiment of the local processing strategy discussed on this page. Roasting, flavouring, premium packaging: value stays in Guinea-Bissau.",
 "A nossa casa de mercearia fina encarna concretamente a estratégia de transformação local abordada nesta página. Torrefacção, aromatização, embalagem premium: o valor permanece na Guiné-Bissau."
 )}
 cta={tt("Découvrir NANDA SÁ", "Discover NANDA SÁ", "Descobrir NANDA SÁ")}
 />
 );
};

const EnergiaProCTA = () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-energiapro"
 to="/marques/energia-pro"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "NANDA SÁ Energia Pro. Solaire résidentiel, professionnel et rural.",
 "NANDA SÁ Energia Pro. Residential, professional and rural solar.",
 "NANDA SÁ Energia Pro. Solar residencial, profissional e rural."
 )}
 description={tt(
 "Panneaux Q.SAVE-G3, batteries MARSTEK Venus A, mini-réseaux, pompage solaire : nous mettons en œuvre les solutions énergétiques évoquées, avec des équipements de dernière génération.",
 "Q.SAVE-G3 panels, MARSTEK Venus A batteries, mini-grids, solar pumping: we deploy the energy solutions mentioned here, with latest-generation equipment.",
 "Painéis Q.SAVE-G3, baterias MARSTEK Venus A, mini-redes, bombagem solar: implementamos as soluções energéticas aqui referidas, com equipamentos de última geração."
 )}
 cta={tt("Découvrir Energia Pro", "Discover Energia Pro", "Descobrir a Energia Pro")}
 />
 );
};

const EleoneCTA = () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-cosmetiques"
 to="/cosmetiques"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "NANDA SÁ Cosmétiques. La cosmétique botanique ouest-africaine.",
 "NANDA SÁ Cosmétiques. West African botanical cosmetics.",
 "NANDA SÁ Cosmétiques. A cosmética botânica oeste-africana."
 )}
 description={tt(
 "L'huile de coque de cajou, le karité, les plantes de la région : nous transformons ces ressources en soins de haute exigence. Une filière de plus, portée par le territoire.",
 "Cashew nut shell oil, shea, plants of the region: we transform these resources into high-standard skincare. Another value chain rooted in the territory.",
 "O óleo da casca do caju, a manteiga de karité, as plantas da região: transformamos estes recursos em cosméticos de exigência. Mais uma fileira, sustentada pelo território."
 )}
 cta={tt("Découvrir NANDA SÁ Cosmétiques", "Discover NANDA SÁ Cosmétiques", "Descobrir NANDA SÁ Cosmétiques")}
 />
 );
};

const AcademieCTA = () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-academie"
 to="/marques/academie"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "NANDA SÁ Académie. Former la génération qui bâtira Canchungo.",
 "NANDA SÁ Academy. Training the generation that will build Canchungo.",
 "NANDA SÁ Academia. Formar a geração que construirá Canchungo."
 )}
 description={tt(
 "Notre pôle éducatif accompagne la jeunesse locale sur les métiers d'avenir : énergie solaire, agroalimentaire, entrepreneuriat, numérique, langues.",
 "Our educational hub supports local youth in the trades of tomorrow: solar energy, agri-food, entrepreneurship, digital, languages.",
 "O nosso pólo educativo acompanha a juventude local nos ofícios do futuro: energia solar, agroalimentar, empreendedorismo, digital, línguas."
 )}
 cta={tt("Découvrir l'Académie", "Discover the Academy", "Descobrir a Academia")}
 />
 );
};

const ConseilCTA = () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-conseil"
 to="/conseil-implantation"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "Conseil en implantation. De l'idée au terrain.",
 "Establishment advisory. From idea to ground.",
 "Consultoria de implantação. Da ideia ao terreno."
 )}
 description={tt(
 "Étude de marché, foncier, démarches administratives, recrutement, partenariats locaux : nous accompagnons les investisseurs et les diasporas dans leur installation en Guinée-Bissau, avec méthode.",
 "Market study, land, administrative procedures, recruitment, local partnerships: we support investors and diasporas in their establishment in Guinea-Bissau, with method.",
 "Estudo de mercado, imobiliário, formalidades administrativas, recrutamento, parcerias locais: acompanhamos os investidores e as diásporas na sua instalação na Guiné-Bissau, com método."
 )}
 cta={tt("Découvrir le Conseil en implantation", "Discover the Establishment Advisory", "Descobrir a Consultoria de implantação")}
 />
 );
};

const ContactCTA = () => {
 const tt = useT();
 return (
 <InlineCTA
 testId="cta-inline-contact"
 to="/contact?subject=Opportunit%C3%A9s%20Guin%C3%A9e-Bissau"
 eyebrow={tt("Passerelle NANDA SÁ", "NANDA SÁ Bridge", "Ponte NANDA SÁ")}
 title={tt(
 "Parlons de votre projet.",
 "Let's talk about your project.",
 "Falemos do seu projecto."
 )}
 description={tt(
 "Investisseur, entreprise, ONG, institution, diaspora : partagez vos intentions, nous répondons personnellement.",
 "Investor, company, NGO, institution, diaspora: share your intentions, we respond personally.",
 "Investidor, empresa, ONG, instituição, diáspora: partilhe as suas intenções, respondemos pessoalmente."
 )}
 cta={tt("Contacter NANDA SÁ", "Contact NANDA SÁ", "Contactar a NANDA SÁ")}
 />
 );
};

const injectJsonLd = (data) => {
 const s = document.createElement("script");
 s.setAttribute("type", "application/ld+json");
 s.setAttribute("data-seo", "opportunites");
 s.textContent = JSON.stringify(data);
 document.head.appendChild(s);
 return s;
};

const useArticleSeo = (title, description, locale) => {
 React.useEffect(() => {
 document.title = title;
 const setMeta = (attr, key, value) => {
 let el = document.head.querySelector(`meta[${attr}="${key}"]`);
 if (!el) {
 el = document.createElement("meta");
 el.setAttribute(attr, key);
 document.head.appendChild(el);
 }
 el.setAttribute("content", value);
 };
 setMeta("name", "description", description);
 setMeta("property", "og:title", title);
 setMeta("property", "og:description", description);
 setMeta("property", "og:image", `${SITE_URL}/photos/canchungo-hero.jpg`);
 setMeta("property", "og:type", "article");
 setMeta("name", "twitter:title", title);
 setMeta("name", "twitter:description", description);

 const scripts = [];
 scripts.push(
 injectJsonLd({
 "@context": "https://schema.org",
 "@type": "Article",
 headline: title,
 description,
 image: `${SITE_URL}/photos/canchungo-hero.jpg`,
 author: { "@type": "Organization", name: "NANDA SÁ" },
 publisher: {
 "@type": "Organization",
 name: "NANDA SÁ",
 logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.svg` },
 },
 datePublished: "2026-02-15",
 dateModified: "2026-02-15",
 inLanguage: locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR",
 mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/opportunites` },
 about: [
 { "@type": "Place", name: "Canchungo, Guinea-Bissau" },
 { "@type": "Place", name: "Cacheu, Guinea-Bissau" },
 { "@type": "Thing", name: "Cashew (Anacardium occidentale)" },
 ],
 keywords:
 "investir en Guinée-Bissau, opportunités en Guinée-Bissau, investir à Canchungo, économie de la Guinée-Bissau, cajou Guinée-Bissau, agriculture Guinée-Bissau, tourisme Guinée-Bissau, énergie solaire Guinée-Bissau, immobilier Guinée-Bissau, développement économique Afrique de l'Ouest, NANDA SÁ, Cacheu, CEDEAO, UEMOA, port de Bissau, transformation agroalimentaire",
 })
 );
 scripts.push(
 injectJsonLd({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: locale === "en" ? "Home" : locale === "pt" ? "Início" : "Accueil", item: `${SITE_URL}/` },
 { "@type": "ListItem", position: 2, name: locale === "en" ? "Journal" : locale === "pt" ? "Caderno" : "Carnet", item: `${SITE_URL}/carnet` },
 { "@type": "ListItem", position: 3, name: title, item: `${SITE_URL}/opportunites` },
 ],
 })
 );
 return () => {
 document.head.querySelectorAll('script[data-seo="opportunites"]').forEach((el) => el.remove);
 };
 }, [title, description, locale]);
};

/* ------------------------------------------------------------------ */
/* Contenu FR */
/* ------------------------------------------------------------------ */
const ContentFR = () => (
 <>
 <Section testId="opp-intro">
 <FadeUp>
 <P>
 Les grandes opportunités économiques ne naissent pas uniquement dans les capitales.
 Elles émergent aussi dans les territoires où la valeur reste encore à créer, où la
 ressource humaine, la ressource naturelle et la volonté politique commencent tout juste
 à se rencontrer. Ces territoires-là, plus discrets, sont souvent les plus prometteurs.
 Canchungo, situé au cœur d'un bassin agricole exceptionnel entre Bissau et les grands axes de la CEDEAO et les grands axes commerciaux de l'Afrique de l'Ouest, appartient à cette
 catégorie de lieux dont on parle peu, mais où beaucoup se construit.
 </P>
 <P>
 Cette page est une invitation. Une invitation à regarder la Guinée-Bissau, la région
 de Cacheu et la ville de Canchungo avec le regard d'un investisseur, d'un entrepreneur,
 d'un exportateur, d'une institution ou d'une diaspora qui cherche un point d'ancrage
 réel. NANDA SÁ, entreprise ancrée en Guinée-Bissau, y accompagne les acteurs
 économiques dans la découverte de ces opportunités, sans slogan et sans exagération,
 en partant de ce que le terrain rend possible.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-geo">
 <FadeUp>
 <H2>Canchungo : une situation géographique exceptionnelle</H2>
 <P>
 Canchungo est la principale ville de la région de Cacheu, au nord-ouest de la
 Guinée-Bissau. Sa position offre un rare croisement d'atouts logistiques,
 commerciaux et agricoles. Au cœur d'un bassin agricole exceptionnel, à moins de deux heures de Bissau, en lien direct avec les régions voisines, la ville ouvre sur les marchés national bissau-guinéen et, plus largement, la CEDEAO.
 </P>
 <H3>Un carrefour naturel au cœur de la Guinée-Bissau</H3>
 <P>
 Canchungo se situe sur les axes qui relient Bissau aux axes du nord et de l'est de la Guinée-Bissau.
 Ces routes sont des voies vivantes, empruntées chaque jour par les producteurs, les
 transporteurs, les commerçants et les familles et communautés locales. Le corridor
 Bissau–Cacheu est déjà l'un des plus dynamiques de la sous-région, et son
 activité continue de croître.
 </P>
 <H3>Une proximité utile avec les ports et la Gambie</H3>
 <P>
 Le port de Bissau donne accès à l'océan Atlantique et aux principales routes
 d'exportation. Plus au nord, les ports secondaires de Cacheu et Buba constituent des alternatives logistiques précieuses. La Gambie, avec Banjul, complète cette
 triangulation portuaire. Pour un acteur qui pense international, cette proximité
 n'est pas anecdotique : elle change la géographie des coûts.
 </P>
 <H3>Un accès direct aux zones rurales productrices</H3>
 <P>
 Canchungo est aussi une porte d'entrée vers les zones rurales productrices de
 la région de Cacheu : bassins d'anacardiers, vergers de manguiers et d'agrumes,
 rizières, exploitations maraîchères, terres pastorales. Cette proximité entre le
 centre urbain et les zones agricoles est un avantage structurel pour toute
 entreprise de transformation, de logistique ou de commerce.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-cajou">
 <FadeUp>
 <H2>La noix de cajou : une opportunité mondiale</H2>
 <P>
 La Guinée-Bissau est l'un des grands producteurs mondiaux de noix de cajou brute.
 Selon les données publiées par la Banque mondiale et par l'ITC (International
 Trade Centre), le pays figure régulièrement parmi les cinq à dix premiers
 producteurs de la planète, avec une production annuelle qui se compte en
 centaines de milliers de tonnes. La cajou représente, selon les campagnes,
 plus de 80 à 90 % des exportations nationales : c'est un pilier de l'économie.
 </P>
 <P>
 Malgré cette place, la majeure partie de la récolte quitte encore le pays à l'état
 brut. La transformation locale reste minoritaire, alors qu'elle constitue la
 principale source de création de valeur. Cette réalité, souvent soulignée par
 la Banque africaine de développement (BAD), la FAO et l'AFD, ouvre un espace
 d'investissement considérable. Chaque tonne transformée localement représente
 des emplois, des revenus, des compétences et des marges qui restent en Guinée-Bissau.
 </P>
 <H3>Une filière aux mille métiers</H3>
 <P>
 Autour de la noix de cajou, chaque étape peut devenir une activité économique à
 part entière. C'est ce qui fait la richesse de la filière : elle ne se réduit
 jamais à un seul geste.
 </P>
 <UL
 items={[
 "Collecte auprès des producteurs et des coopératives",
 "Stockage sécurisé, ventilé, tracé",
 "Séchage et calibrage",
 "Décorticage industriel ou semi-industriel",
 "Torréfaction et aromatisation",
 "Conditionnement et emballage premium",
 "Export vers l'Europe, les États-Unis, le Moyen-Orient et l'Asie",
 "Logistique portuaire et documentaire",
 "Beurre de cajou pour la nutrition et la pâtisserie",
 "Boissons végétales à base de cajou",
 "Farine de cajou pour la boulangerie et la biscuiterie",
 "Huile issue de la coque, valorisée en industrie",
 "Biomasse énergétique à partir des résidus",
 "Alimentation animale à partir des sous-produits",
 "Ingrédients cosmétiques issus des huiles",
 "Artisanat utilisant les coques et les enveloppes",
 ]}
 />
 <P>
 De la parcelle à la table, ce sont des dizaines de métiers qui coexistent.
 Développer un seul de ces maillons, c'est déjà entrer dans une chaîne de valeur
 mondiale.
 </P>
 </FadeUp>
 <NandaSaFoodCTA />
 <EleoneCTA />
 </Section>

 <Section testId="opp-secteurs">
 <FadeUp>
 <H2>Les autres secteurs à fort potentiel</H2>
 <P>
 La cajou n'est pas seule. Autour d'elle, plusieurs filières se dessinent, portées
 par la richesse écologique de la région, la jeunesse de la population et la
 demande croissante des marchés voisins.
 </P>
 <H3>Agriculture</H3>
 <UL
 items={[
 "Mangues, régulièrement citées parmi les meilleures d'Afrique de l'Ouest",
 "Agrumes destinés au marché local et régional",
 "Anacardiers, autour d'un savoir-faire séculaire",
 "Maraîchage pour l'approvisionnement urbain",
 "Riz, culture traditionnelle et stratégique",
 "Fruits tropicaux (papaye, banane, ananas, corossol)",
 ]}
 />
 <H3>Élevage</H3>
 <UL
 items={[
 "Filières bovines et laitières",
 "Élevage ovin et caprin",
 "Aviculture pour l'auto-approvisionnement en protéines",
 "Apiculture et miels de forêt",
 ]}
 />
 <H3>Pêche</H3>
 <UL
 items={[
 "Pêche artisanale et semi-industrielle",
 "Transformation (salage, séchage, fumage, surgélation)",
 "Conservation et logistique du froid",
 "Export vers l'Europe et l'Afrique de l'Ouest",
 ]}
 />
 <H3>Énergies renouvelables</H3>
 <UL
 items={[
 "Solaire photovoltaïque résidentiel et professionnel",
 "Mini-réseaux ruraux pour l'électrification décentralisée",
 "Pompage solaire pour l'agriculture et l'accès à l'eau",
 "Éclairage public solaire",
 "Stockage lithium pour la stabilité des réseaux",
 ]}
 />
 <H3>Immobilier et infrastructures</H3>
 <UL
 items={[
 "Commerces et surfaces professionnelles",
 "Entrepôts logistiques et chambres froides",
 "Logements pour la classe moyenne émergente",
 "Hôtellerie et tourisme rural",
 ]}
 />
 <H3>Artisanat et industries légères</H3>
 <UL
 items={[
 "Textile et confection",
 "Bois et menuiserie contemporaine",
 "Vannerie et objets d'art",
 "Transformation locale des matières premières",
 ]}
 />
 </FadeUp>
 <EnergiaProCTA />
 </Section>

 <Section testId="opp-regions">
 <FadeUp>
 <H2>Pourquoi la position stratégique de Canchungo change tout</H2>
 <P>
 Canchungo bénéficie d'un avantage rarement mis en avant : sa proximité immédiate
 avec la Guinée-Bissau. Cette proximité n'est pas seulement géographique,
 elle est culturelle, linguistique et économique. Les familles, les langues, les
 savoir-faire et les marchés se recoupent sur cet axe transfrontalier.
 </P>
 <P>
 Pour un investisseur, cela se traduit concrètement par un accès facilité aux marchés de la CEDEAO, une complémentarité économique évidente entre les régions productrices, une circulation naturelle des compétences et des savoir-faire, et des
 opportunités logistiques significatives autour du port de Bissau et
 des corridors régionaux de la CEDEAO.
 </P>
 <P>
 Canchungo peut ainsi devenir une véritable plateforme régionale, un point d'ancrage stratégique pour toute la région ouest-africaine. Ce rôle de trait d'union est
 rare, et il constitue un atout stratégique pour toute entreprise qui pense
 au-delà des frontières.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-education">
 <FadeUp>
 <H2>L'éducation : le meilleur investissement</H2>
 <P>
 Le développement économique repose autant sur les ressources naturelles que sur
 les compétences humaines. Toute croissance durable suppose une élévation continue
 du niveau de formation. À Canchungo comme ailleurs en Afrique de l'Ouest, c'est
 souvent là que se joue la différence entre une opportunité entrevue et une
 opportunité réalisée.
 </P>
 <P>
 Investir dans le capital humain, c'est investir dans plusieurs domaines à la fois :
 formation professionnelle, apprentissage des métiers techniques, entrepreneuriat,
 compétences numériques, agriculture moderne, énergie solaire, transformation
 agroalimentaire, commerce international, apprentissage des langues, partenariats
 avec les universités et transfert de compétences. Aucune économie durable ne s'est
 jamais construite sans école, sans centre de formation et sans passeurs de savoir.
 </P>
 <P>
 C'est cette conviction qui a conduit NANDA SÁ à créer, aux côtés de ses activités
 commerciales, un pôle éducatif dédié à la jeunesse de la région. Non par vocation
 philanthropique, mais parce que l'éducation est la première infrastructure
 économique d'un territoire.
 </P>
 </FadeUp>
 <AcademieCTA />
 </Section>

 <Section testId="opp-chiffres">
 <FadeUp>
 <H2>Les chiffres qui parlent</H2>
 <P>
 Quelques repères, tirés de sources publiques (Institut national de la statistique
 de la Guinée-Bissau, Banque mondiale, ITC, FAO, BAD, AFD), permettent de cadrer
 l'ordre de grandeur des opportunités.
 </P>
 <UL
 items={[
 "Population de la Guinée-Bissau : environ 2,1 millions d'habitants",
 "Région de Cacheu : autour de 220 000 habitants, l'une des plus peuplées du pays",
 "Canchungo : environ 15 000 à 25 000 habitants selon les recensements récents et les estimations locales",
 "Guinée-Bissau : régulièrement classée parmi les grands producteurs mondiaux de noix de cajou (entre le 4ᵉ et le 8ᵉ rang selon les campagnes)",
 "Part de la cajou dans les exportations nationales : de 80 à 90 % selon les années",
 "Croissance économique récente : autour de 4 à 5 % par an sur la période 2022-2025 selon la Banque mondiale",
 "Ensoleillement : environ 2 500 à 3 000 heures par an, l'un des potentiels solaires les plus élevés de la sous-région",
 "Population jeune : plus de 60 % des habitants ont moins de 25 ans",
 "Corridor Bissau–Cacheu : l'un des axes économiques les plus actifs de la Guinée-Bissau",
 ]}
 />
 <P>
 Ces chiffres, croisés avec la faible saturation actuelle des marchés locaux, dessinent
 un potentiel de croissance rare à cette échelle en Afrique de l'Ouest.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-invest">
 <FadeUp>
 <H2>Pourquoi investir aujourd'hui</H2>
 <P>
 Investir à Canchungo et dans la région de Cacheu aujourd'hui, c'est arriver au
 moment où plusieurs conditions se rejoignent. Les marchés sont encore peu
 saturés dans la plupart des filières. Les coûts d'installation, de foncier et
 de main-d'œuvre restent compétitifs. Les ressources naturelles — cajou, mangue,
 riz, sel, poisson, soleil — sont abondantes.
 </P>
 <P>
 Les besoins en transformation locale sont considérables, aussi bien pour
 répondre à la demande intérieure que pour capter la valeur qui, jusqu'ici,
 quittait le pays. La proximité avec la Guinée-Bissau offre un accès rapide à un
 marché plus mature et à des chaînes logistiques déjà organisées. Enfin, les
 perspectives de croissance, portées par la démographie et par l'intégration
 régionale, sont parmi les plus solides de la sous-région.
 </P>
 <P>
 Autrement dit, tout ce que les investisseurs recherchent — un marché peu couvert,
 des ressources abondantes, une main-d'œuvre jeune, une proximité stratégique
 avec un pays voisin plus développé et une trajectoire de croissance — se
 retrouve ici.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-nandasa">
 <FadeUp>
 <H2>Le rôle de NANDA SÁ</H2>
 <P>
 NANDA SÁ n'est pas un acteur de plus. C'est une entreprise ancrée à Canchungo,
 présente sur le terrain, en lien direct avec les producteurs, les coopératives,
 les institutions locales, les autorités régionales et les partenaires
 internationaux. Elle agit dans plusieurs métiers complémentaires : énergie
 solaire, équipements professionnels, épicerie fine autour de l'anacarde,
 cosmétiques botaniques, formation, art de vivre.
 </P>
 <P>
 Ce positionnement en fait un facilitateur naturel pour les investisseurs
 étrangers, les diasporas et les institutions qui souhaitent découvrir la région
 sans repartir de zéro. NANDA SÁ oriente, met en relation, contextualise,
 co-construit. Elle n'a pas vocation à remplacer les opérateurs locaux ; elle
 cherche à en rapprocher davantage, plus vite, avec plus de rigueur.
 </P>
 </FadeUp>
 <div className="mx-auto max-w-[880px] px-6 md:px-10">
 <ConseilCTA />
 <ContactCTA />
 </div>
 </Section>

 <Section testId="opp-conclusion">
 <FadeUp>
 <H2>Conclusion</H2>
 <P>
 Les territoires d'avenir ne sont pas toujours ceux dont on parle le plus. Ce sont
 souvent ceux où les ressources, les femmes, les hommes et les idées se rencontrent
 pour créer la valeur de demain. Canchungo, dans la région de Cacheu, en Guinée-Bissau,
 est de ces territoires. Encore discret, encore en construction, mais déjà porté
 par tout ce qui compte : la terre, la mer, une jeunesse nombreuse, une position
 géographique rare et un rôle naturel de passerelle entre deux pays.
 </P>
 <P className="font-serif text-2xl md:text-3xl italic text-nanda-ink mt-10">
 Les opportunités existent partout. Elles attendent simplement celles et ceux
 qui savent les reconnaître, les construire et les partager.
 </P>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link
 to="/contact?subject=Investir%20avec%20NANDA%20SÁ"
 data-testid="opp-cta-contact"
 className="btn-arrow inline-flex items-center gap-3 bg-nanda-ink text-white px-7 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
 >
 Investir avec NANDA SÁ
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link
 to="/secteurs"
 data-testid="opp-cta-secteurs"
 className="btn-arrow inline-flex items-center gap-3 bg-transparent border border-nanda-ink text-nanda-ink px-7 py-4 hover:bg-nanda-ink hover:text-white transition-colors duration-500 label-overline"
 >
 Explorer nos secteurs
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </>
);

/* ------------------------------------------------------------------ */
/* Contenu EN */
/* ------------------------------------------------------------------ */
const ContentEN = () => (
 <>
 <Section testId="opp-intro">
 <FadeUp>
 <P>
 The greatest economic opportunities do not always emerge in capitals. They also arise
 in territories where value has yet to be created, where human capital, natural
 resources and political will are only just beginning to meet. These quieter
 territories are often the most promising ones. Canchungo, at the heart of an
 exceptional agricultural basin between Bissau, Bissau-Guinean Guinée-Bissau and the major
 West African trade routes, belongs to this category of places one hears little
 about, but where much is being built.
 </P>
 <P>
 This page is an invitation. An invitation to look at Guinea-Bissau, the Cacheu region
 and the town of Canchungo through the eyes of an investor, entrepreneur, exporter,
 institution or diaspora seeking a real foothold. NANDA SÁ, a company based in
 Guinea-Bissau, supports economic players in discovering these opportunities,
 without slogans and without exaggeration, starting from what the field truly allows.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-geo">
 <FadeUp>
 <H2>Canchungo: an exceptional geography</H2>
 <P>
 Canchungo is the main town of the Cacheu region, in north-western Guinea-Bissau.
 Its position offers a rare combination of logistical, commercial and agricultural
 strengths. A few dozen kilometres from the Bissau-Guinean border, less than two hours
 from Bissau, in direct connection with and Guinée-Bissau, the town opens
 onto three markets at once: the Bissau-Guinean national market, the Bissau-Guinean
 market and, more broadly, ECOWAS.
 </P>
 <H3>A natural crossroads between Bissau and Guinée-Bissau</H3>
 <P>
 Canchungo sits on the roads that connect Bissau to and Guinée-Bissau.
 These are living routes, used daily by producers, transporters, traders and
 local families and communities. The Bissau–corridor is already one of the most
 dynamic in the sub-region, and its activity continues to grow.
 </P>
 <H3>Useful proximity to ports and to Gambia</H3>
 <P>
 The port of Bissau provides access to the Atlantic Ocean and to major export
 routes. Further north and Bissau act as valuable logistical
 alternatives. Gambia, with Banjul, completes this port triangulation.
 For an internationally minded operator, this proximity is not anecdotal:
 it reshapes the geography of costs.
 </P>
 <H3>Direct access to productive rural areas</H3>
 <P>
 Canchungo is also a gateway to the productive rural areas of Cacheu: cashew
 orchards, mango and citrus groves, rice fields, market gardens, pastoral lands.
 This proximity between urban centre and agricultural land is a structural
 advantage for any processing, logistics or trade company.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-cajou">
 <FadeUp>
 <H2>Cashew: a global opportunity</H2>
 <P>
 Guinea-Bissau is one of the world's leading producers of raw cashew nuts.
 According to World Bank and ITC (International Trade Centre) data, the country
 regularly ranks among the top five to ten producers globally, with annual
 production measured in hundreds of thousands of tonnes. Cashew represents,
 depending on the campaign, more than 80 to 90% of national exports: it is
 a pillar of the economy.
 </P>
 <P>
 Despite this position, most of the harvest still leaves the country in raw form.
 Local processing remains a minority activity, whereas it is the main source of
 value creation. This reality, regularly highlighted by the African Development
 Bank (AfDB), FAO and AFD, opens up a considerable investment window. Every tonne
 processed locally means jobs, income, skills and margins that remain in Guinea-Bissau.
 </P>
 <H3>A value chain of a thousand trades</H3>
 <P>
 Around cashew, every step can become a full economic activity of its own. That
 is what makes the chain so rich: it is never reduced to a single gesture.
 </P>
 <UL
 items={[
 "Collection from producers and cooperatives",
 "Secure, ventilated and traced storage",
 "Drying and sizing",
 "Industrial or semi-industrial shelling",
 "Roasting and flavouring",
 "Premium packaging and conditioning",
 "Export to Europe, the United States, the Middle East and Asia",
 "Port and documentary logistics",
 "Cashew butter for nutrition and pastry",
 "Plant-based cashew beverages",
 "Cashew flour for bakery and biscuits",
 "Cashew nut shell oil for industry",
 "Energy biomass from residues",
 "Animal feed from by-products",
 "Cosmetic ingredients derived from oils",
 "Craftsmanship using shells and envelopes",
 ]}
 />
 <P>
 From plot to table, dozens of trades coexist. Developing just one of these
 links is already entering a global value chain.
 </P>
 </FadeUp>
 <NandaSaFoodCTA />
 <EleoneCTA />
 </Section>

 <Section testId="opp-secteurs">
 <FadeUp>
 <H2>Other high-potential sectors</H2>
 <P>
 Cashew is not alone. Around it, several other value chains take shape, supported
 by the region's ecological wealth, a young population and growing demand from
 neighbouring markets.
 </P>
 <H3>Agriculture</H3>
 <UL
 items={[
 "Mangoes, regularly cited among the best in West Africa",
 "Citrus for local and regional markets",
 "Cashew trees, built on centuries-old know-how",
 "Market gardening for urban supply",
 "Rice, a traditional and strategic crop",
 "Tropical fruits (papaya, banana, pineapple, soursop)",
 ]}
 />
 <H3>Livestock</H3>
 <UL
 items={[
 "Cattle and dairy value chains",
 "Sheep and goat farming",
 "Poultry for protein self-sufficiency",
 "Beekeeping and forest honey",
 ]}
 />
 <H3>Fisheries</H3>
 <UL
 items={[
 "Artisanal and semi-industrial fishing",
 "Processing (salting, drying, smoking, freezing)",
 "Cold chain and conservation",
 "Export to Europe and West Africa",
 ]}
 />
 <H3>Renewable energy</H3>
 <UL
 items={[
 "Residential and professional photovoltaic solar",
 "Rural mini-grids for decentralised electrification",
 "Solar pumping for agriculture and water access",
 "Solar street lighting",
 "Lithium storage for grid stability",
 ]}
 />
 <H3>Real estate and infrastructure</H3>
 <UL
 items={[
 "Retail and professional space",
 "Logistics warehouses and cold rooms",
 "Housing for an emerging middle class",
 "Hospitality and rural tourism",
 ]}
 />
 <H3>Crafts and light industry</H3>
 <UL
 items={[
 "Textiles and clothing",
 "Wood and contemporary joinery",
 "Basketry and art objects",
 "Local processing of raw materials",
 ]}
 />
 </FadeUp>
 <EnergiaProCTA />
 </Section>

 <Section testId="opp-regions">
 <FadeUp>
 <H2>Why proximity to Guinea-Bissau changes everything</H2>
 <P>
 Canchungo enjoys a rarely highlighted advantage: its immediate proximity to
 Bissau-Guinean Guinée-Bissau. This proximity is not only geographical, it is cultural,
 linguistic and economic. Families, languages, know-how and markets overlap on
 this cross-border axis.
 </P>
 <P>
 For an investor, this translates concretely into easier access to the Bissau-Guinean
 market, an obvious economic complementarity between the productions of the two
 countries, a natural circulation of skills and know-how, and significant
 logistical opportunities around the port of Bissau and the regional
 ECOWAS corridors.
 </P>
 <P>
 Canchungo can thus become a true regional platform, a meeting point between
 Guinea-Bissau. This role of bridge is rare, and it constitutes a
 strategic asset for any company that thinks beyond borders.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-education">
 <FadeUp>
 <H2>Education: the best investment</H2>
 <P>
 Economic development rests as much on natural resources as on human skills.
 Any sustainable growth requires a continuous rise in training levels. In
 Canchungo, as elsewhere in West Africa, this is often what makes the difference
 between an opportunity glimpsed and an opportunity fulfilled.
 </P>
 <P>
 Investing in human capital means investing in several fields at once: vocational
 training, apprenticeship in technical trades, entrepreneurship, digital skills,
 modern agriculture, solar energy, agri-food processing, international trade,
 language learning, partnerships with universities and skills transfer. No lasting
 economy has ever been built without schools, training centres and transmitters
 of knowledge.
 </P>
 <P>
 This conviction has led NANDA SÁ, alongside its commercial activities, to
 create an educational programme dedicated to the region's youth. Not out of
 philanthropic vocation, but because education is the first economic
 infrastructure of any territory.
 </P>
 </FadeUp>
 <AcademieCTA />
 </Section>

 <Section testId="opp-chiffres">
 <FadeUp>
 <H2>The numbers that speak</H2>
 <P>
 A few markers, drawn from public sources (Guinea-Bissau National Institute of
 Statistics, World Bank, ITC, FAO, AfDB, AFD), help frame the order of magnitude
 of the opportunities.
 </P>
 <UL
 items={[
 "Guinea-Bissau population: about 2.1 million inhabitants",
 "Cacheu region: around 220,000 inhabitants, one of the most populous in the country",
 "Canchungo: approximately 15,000 to 25,000 inhabitants according to recent censuses and local estimates",
 "Guinea-Bissau: regularly ranked among the world's top cashew producers (4th to 8th depending on the season)",
 "Cashew share of national exports: between 80 and 90% depending on the year",
 "Recent economic growth: around 4 to 5% per year over 2022-2025 according to the World Bank",
 "Sunshine: about 2,500 to 3,000 hours per year, among the highest solar potentials in the sub-region",
 "Young population: over 60% of inhabitants under 25 years old",
 "Bissau–corridor: one of the most active cross-border axes between Guinea-Bissau",
 ]}
 />
 <P>
 These figures, combined with the current low saturation of local markets, outline
 a growth potential rare at this scale in West Africa.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-invest">
 <FadeUp>
 <H2>Why invest today</H2>
 <P>
 Investing in Canchungo and the Cacheu region today means arriving at the moment
 when several conditions align. Most local markets are still lightly served.
 Installation, land and labour costs remain competitive. Natural resources —
 cashew, mango, rice, salt, fish, sun — are abundant.
 </P>
 <P>
 Local processing needs are considerable, both to meet domestic demand and to
 capture the value that has, until now, been leaving the country. Proximity to
 Guinea-Bissau offers quick access to a more mature market and to already organised
 logistics chains. Finally, growth prospects, driven by demography and regional
 integration, are among the strongest in the sub-region.
 </P>
 <P>
 In other words, everything investors look for — a lightly covered market, abundant
 resources, a young workforce, strategic proximity to a more developed neighbouring
 country and a growth trajectory — can be found here.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-nandasa">
 <FadeUp>
 <H2>The role of NANDA SÁ</H2>
 <P>
 NANDA SÁ is not just another operator. It is a company anchored in Canchungo,
 present on the ground, in direct contact with producers, cooperatives, local
 institutions, regional authorities and international partners. It operates
 across several complementary trades: solar energy, professional equipment,
 fine foods around cashew, botanical cosmetics, training, lifestyle.
 </P>
 <P>
 This positioning makes it a natural facilitator for foreign investors,
 diasporas and institutions wishing to discover the region without starting from
 scratch. NANDA SÁ orients, connects, contextualises, co-builds. It has no
 intention of replacing local operators; it seeks to bring them closer, faster
 and with greater rigour.
 </P>
 </FadeUp>
 <div className="mx-auto max-w-[880px] px-6 md:px-10">
 <ConseilCTA />
 <ContactCTA />
 </div>
 </Section>

 <Section testId="opp-conclusion">
 <FadeUp>
 <H2>Conclusion</H2>
 <P>
 The territories of the future are not always those we hear most about. They are
 often the ones where resources, women, men and ideas meet to create the value of
 tomorrow. Canchungo, in the Cacheu region of Guinea-Bissau, is one of those
 territories. Still quiet, still under construction, but already carried by
 everything that counts: land, sea, a large youth, a rare geography and a natural
 role of bridge between two countries.
 </P>
 <P className="font-serif text-2xl md:text-3xl italic text-nanda-ink mt-10">
 Opportunities exist everywhere. They simply await those who know how to recognise
 them, build them and share them.
 </P>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link
 to="/contact?subject=Invest%20with%20NANDA%20S%C3%81"
 className="btn-arrow inline-flex items-center gap-3 bg-nanda-ink text-white px-7 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
 >
 Invest with NANDA SÁ
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link
 to="/secteurs"
 className="btn-arrow inline-flex items-center gap-3 bg-transparent border border-nanda-ink text-nanda-ink px-7 py-4 hover:bg-nanda-ink hover:text-white transition-colors duration-500 label-overline"
 >
 Explore our sectors
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </>
);

/* ------------------------------------------------------------------ */
/* Contenu PT */
/* ------------------------------------------------------------------ */
const ContentPT = () => (
 <>
 <Section testId="opp-intro">
 <FadeUp>
 <P>
 As grandes oportunidades económicas não nascem apenas nas capitais. Emergem também
 nos territórios onde o valor está ainda por criar, onde o recurso humano, o
 recurso natural e a vontade política começam apenas a encontrar-se. Esses
 territórios, mais discretos, são frequentemente os mais promissores. Canchungo,
 situado no coração de um excepcional bacião agrícola entre Bissau, a Guiné-Bissau
 e os grandes eixos comerciais da África Ocidental, pertence a essa
 categoria de lugares de que pouco se fala, mas onde muito se constrói.
 </P>
 <P>
 Esta página é um convite. Um convite a olhar para a Guiné-Bissau, para a região
 de Cacheu e para a cidade de Canchungo com os olhos de um investidor, de um
 empresário, de um exportador, de uma instituição ou de uma diáspora que procura
 um verdadeiro ponto de ancoragem. A NANDA SÁ, empresa implantada na Guiné-Bissau,
 acompanha os actores económicos na descoberta destas oportunidades, sem slogans e
 sem exageros, a partir do que o terreno realmente permite.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-geo">
 <FadeUp>
 <H2>Canchungo: uma geografia excepcional</H2>
 <P>
 Canchungo é a principal cidade da região de Cacheu, no noroeste da Guiné-Bissau.
 A sua posição oferece uma rara combinação de trunfos logísticos, comerciais e
 agrícolas. No coração de uma bacia agrícola excepcional, a menos de
 duas horas de Bissau, em ligação directa com e a Guiné-Bissau, a cidade
 abre-se sobre três mercados em simultâneo: o mercado nacional guineense, o
 mercados da CEDEAO e, mais amplamente, a CEDEAO.
 </P>
 <H3>Um cruzamento natural entre Bissau e Guiné-Bissau</H3>
 <P>
 Canchungo situa-se nos eixos que ligam Bissau a e à Guiné-Bissau. São
 vias vivas, percorridas todos os dias por produtores, transportadores, comerciantes
 e famílias e comunidades locais. O corredor Bissau–é já um dos mais dinâmicos
 da sub-região, e a sua actividade continua a crescer.
 </P>
 <H3>Proximidade útil com os portos e com a Gâmbia</H3>
 <P>
 O porto de Bissau dá acesso ao Oceano Atlântico e às principais rotas de
 exportação. Mais a norte e Bissau constituem alternativas logísticas
 valiosas. A Gâmbia, com Banjul, completa esta triangulação portuária. Para um
 actor com visão internacional, esta proximidade não é anedótica: altera a
 geografia dos custos.
 </P>
 <H3>Acesso directo às zonas rurais produtivas</H3>
 <P>
 Canchungo é também uma porta de entrada para as zonas rurais produtivas de Cacheu:
 bacias de cajueiros, pomares de mangueiras e citrinos, arrozais, hortas,
 terras pastorais. Esta proximidade entre o centro urbano e o campo constitui
 uma vantagem estrutural para qualquer empresa de transformação, logística ou
 comércio.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-cajou">
 <FadeUp>
 <H2>O caju: uma oportunidade mundial</H2>
 <P>
 A Guiné-Bissau é um dos maiores produtores mundiais de castanha de caju em bruto.
 Segundo dados publicados pelo Banco Mundial e pelo ITC (International Trade
 Centre), o país figura regularmente entre os cinco a dez maiores produtores do
 planeta, com uma produção anual que se conta em centenas de milhares de toneladas.
 O caju representa, consoante a campanha, mais de 80 a 90% das exportações
 nacionais: é um pilar da economia.
 </P>
 <P>
 Apesar deste lugar, a maior parte da colheita ainda sai do país em bruto. A
 transformação local continua minoritária, quando é a principal fonte de criação
 de valor. Esta realidade, sublinhada pelo Banco Africano de Desenvolvimento
 (BAD), pela FAO e pela AFD, abre um espaço de investimento considerável. Cada
 tonelada transformada localmente representa empregos, rendimentos, competências
 e margens que permanecem na Guiné-Bissau.
 </P>
 <H3>Uma fileira de mil ofícios</H3>
 <P>
 Em torno da castanha de caju, cada etapa pode tornar-se uma actividade económica
 por inteiro. É essa a riqueza da fileira: nunca se reduz a um único gesto.
 </P>
 <UL
 items={[
 "Recolha junto dos produtores e das cooperativas",
 "Armazenamento seguro, ventilado, rastreado",
 "Secagem e calibragem",
 "Descasque industrial ou semi-industrial",
 "Torrefacção e aromatização",
 "Embalagem premium",
 "Exportação para a Europa, Estados Unidos, Médio Oriente e Ásia",
 "Logística portuária e documental",
 "Manteiga de caju para nutrição e pastelaria",
 "Bebidas vegetais à base de caju",
 "Farinha de caju para panificação e biscoitaria",
 "Óleo da casca da castanha valorizado na indústria",
 "Biomassa energética a partir dos resíduos",
 "Alimentação animal a partir dos subprodutos",
 "Ingredientes cosméticos a partir dos óleos",
 "Artesanato utilizando cascas e películas",
 ]}
 />
 <P>
 Da parcela à mesa, coexistem dezenas de ofícios. Desenvolver apenas um destes
 elos é já entrar numa cadeia de valor mundial.
 </P>
 </FadeUp>
 <NandaSaFoodCTA />
 <EleoneCTA />
 </Section>

 <Section testId="opp-secteurs">
 <FadeUp>
 <H2>Outros sectores de forte potencial</H2>
 <P>
 O caju não está sozinho. À sua volta, várias fileiras desenham-se, sustentadas pela
 riqueza ecológica da região, pela juventude da população e pela procura crescente
 dos mercados vizinhos.
 </P>
 <H3>Agricultura</H3>
 <UL
 items={[
 "Mangas, regularmente citadas entre as melhores da África Ocidental",
 "Citrinos para o mercado local e regional",
 "Cajueiros, em torno de um saber secular",
 "Horticultura para o abastecimento urbano",
 "Arroz, cultura tradicional e estratégica",
 "Frutos tropicais (papaia, banana, ananás, graviola)",
 ]}
 />
 <H3>Pecuária</H3>
 <UL
 items={[
 "Fileiras bovinas e leiteiras",
 "Ovinos e caprinos",
 "Avicultura para autossuficiência proteica",
 "Apicultura e méis de floresta",
 ]}
 />
 <H3>Pescas</H3>
 <UL
 items={[
 "Pesca artesanal e semi-industrial",
 "Transformação (salga, secagem, fumagem, congelação)",
 "Cadeia de frio e conservação",
 "Exportação para a Europa e a África Ocidental",
 ]}
 />
 <H3>Energias renováveis</H3>
 <UL
 items={[
 "Solar fotovoltaico residencial e profissional",
 "Mini-redes rurais para electrificação descentralizada",
 "Bombagem solar para agricultura e acesso à água",
 "Iluminação pública solar",
 "Armazenamento de lítio para estabilidade das redes",
 ]}
 />
 <H3>Imobiliário e infra-estruturas</H3>
 <UL
 items={[
 "Comércios e superfícies profissionais",
 "Armazéns logísticos e câmaras frigoríficas",
 "Habitação para uma classe média emergente",
 "Hotelaria e turismo rural",
 ]}
 />
 <H3>Artesanato e indústrias ligeiras</H3>
 <UL
 items={[
 "Têxtil e confecção",
 "Madeira e marcenaria contemporânea",
 "Cestaria e objectos de arte",
 "Transformação local das matérias-primas",
 ]}
 />
 </FadeUp>
 <EnergiaProCTA />
 </Section>

 <Section testId="opp-regions">
 <FadeUp>
 <H2>Porque a proximidade com o Guinea-Bissau muda tudo</H2>
 <P>
 Canchungo beneficia de uma vantagem raramente destacada: a sua proximidade imediata
 com o coração da região de Cacheu. Essa posição não é apenas geográfica, é cultural,
 linguística e económica. As famílias, as línguas, os saberes e os mercados
 cruzam-se sobre este eixo transfronteiriço.
 </P>
 <P>
 Para um investidor, tal traduz-se concretamente num acesso facilitado ao mercado
 regionais da CEDEAO, numa complementaridade económica evidente entre as regiões produtoras
 países, numa circulação natural de competências e saberes e em oportunidades
 logísticas significativas em torno de do porto de Bissau e dos
 corredores regionais da CEDEAO.
 </P>
 <P>
 Canchungo pode assim tornar-se numa verdadeira plataforma regional, um ponto de
 encontro entre a Guiné-Bissau. Este papel de traço de união é raro
 e constitui um trunfo estratégico para qualquer empresa que pense para além das
 fronteiras.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-education">
 <FadeUp>
 <H2>A educação: o melhor investimento</H2>
 <P>
 O desenvolvimento económico assenta tanto nos recursos naturais como nas
 competências humanas. Qualquer crescimento duradouro pressupõe uma elevação
 contínua do nível de formação. Em Canchungo, como em toda a África Ocidental,
 é frequentemente aí que se joga a diferença entre uma oportunidade entrevista e
 uma oportunidade concretizada.
 </P>
 <P>
 Investir no capital humano é investir em vários domínios ao mesmo tempo:
 formação profissional, aprendizagem de ofícios técnicos, empreendedorismo,
 competências digitais, agricultura moderna, energia solar, transformação
 agroalimentar, comércio internacional, aprendizagem de línguas, parcerias com
 as universidades e transferência de competências. Nenhuma economia duradoura
 foi jamais construída sem escolas, sem centros de formação e sem transmissores
 de saber.
 </P>
 <P>
 É esta convicção que levou a NANDA SÁ a criar, ao lado das suas actividades
 comerciais, um pólo educativo dedicado à juventude da região. Não por vocação
 filantrópica, mas porque a educação é a primeira infra-estrutura económica de
 um território.
 </P>
 </FadeUp>
 <AcademieCTA />
 </Section>

 <Section testId="opp-chiffres">
 <FadeUp>
 <H2>Os números que falam</H2>
 <P>
 Alguns dados, retirados de fontes públicas (Instituto Nacional de Estatística da
 Guiné-Bissau, Banco Mundial, ITC, FAO, BAD, AFD), permitem enquadrar a ordem de
 grandeza das oportunidades.
 </P>
 <UL
 items={[
 "População da Guiné-Bissau: cerca de 2,1 milhões de habitantes",
 "Região de Cacheu: cerca de 220 000 habitantes, uma das mais populosas do país",
 "Canchungo: cerca de 15 000 a 25 000 habitantes, segundo os censos recentes e as estimativas locais",
 "Guiné-Bissau: regularmente classificada entre os maiores produtores mundiais de caju (do 4.º ao 8.º lugar consoante as campanhas)",
 "Peso do caju nas exportações nacionais: 80 a 90% consoante o ano",
 "Crescimento económico recente: cerca de 4 a 5% ao ano no período 2022-2025 segundo o Banco Mundial",
 "Insolação: cerca de 2 500 a 3 000 horas por ano, um dos maiores potenciais solares da sub-região",
 "População jovem: mais de 60% dos habitantes com menos de 25 anos",
 "Corredor Bissau–Bafatá: um dos eixos transfronteiriços mais activos entre a Guiné-Bissau",
 ]}
 />
 <P>
 Estes números, cruzados com a fraca saturação actual dos mercados locais, desenham
 um potencial de crescimento raro nesta escala em África Ocidental.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-invest">
 <FadeUp>
 <H2>Porquê investir hoje</H2>
 <P>
 Investir em Canchungo e na região de Cacheu hoje é chegar no momento em que várias
 condições se juntam. Os mercados estão ainda pouco saturados na maioria das
 fileiras. Os custos de instalação, fundiários e de mão-de-obra permanecem
 competitivos. Os recursos naturais — caju, manga, arroz, sal, peixe, sol — são
 abundantes.
 </P>
 <P>
 As necessidades de transformação local são consideráveis, quer para responder à
 procura interna, quer para captar o valor que, até agora, saía do país. A
 proximidade com o Guinea-Bissau oferece um acesso rápido a um mercado mais maduro e a
 cadeias logísticas já organizadas. Enfim, as perspectivas de crescimento,
 impulsionadas pela demografia e pela integração regional, são das mais sólidas
 da sub-região.
 </P>
 <P>
 Por outras palavras, tudo o que os investidores procuram — um mercado pouco
 coberto, recursos abundantes, uma mão-de-obra jovem, uma proximidade estratégica
 com um país vizinho mais desenvolvido e uma trajectória de crescimento — encontra-se
 aqui.
 </P>
 </FadeUp>
 </Section>

 <Section testId="opp-nandasa">
 <FadeUp>
 <H2>O papel da NANDA SÁ</H2>
 <P>
 A NANDA SÁ não é mais um actor. É uma empresa ancorada em Canchungo, presente no
 terreno, em ligação directa com os produtores, as cooperativas, as instituições
 locais, as autoridades regionais e os parceiros internacionais. Actua em vários
 ofícios complementares: energia solar, equipamentos profissionais, mercearia fina
 em torno do caju, cosméticos botânicos, formação, arte de viver.
 </P>
 <P>
 Este posicionamento faz dela um facilitador natural para investidores estrangeiros,
 diásporas e instituições que desejem descobrir a região sem começar do zero. A
 NANDA SÁ orienta, põe em contacto, contextualiza, co-constrói. Não pretende
 substituir os operadores locais; procura aproximá-los, mais depressa e com mais rigor.
 </P>
 </FadeUp>
 <div className="mx-auto max-w-[880px] px-6 md:px-10">
 <ConseilCTA />
 <ContactCTA />
 </div>
 </Section>

 <Section testId="opp-conclusion">
 <FadeUp>
 <H2>Conclusão</H2>
 <P>
 Os territórios de futuro nem sempre são aqueles de que mais se fala. São, muitas
 vezes, aqueles onde os recursos, as mulheres, os homens e as ideias se encontram
 para criar o valor de amanhã. Canchungo, na região de Cacheu, na Guiné-Bissau, é
 um desses territórios. Ainda discreto, ainda em construção, mas já sustentado por
 tudo o que conta: a terra, o mar, uma juventude numerosa, uma posição geográfica
 rara e um papel natural de ponte entre dois países.
 </P>
 <P className="font-serif text-2xl md:text-3xl italic text-nanda-ink mt-10">
 As oportunidades existem em toda a parte. Esperam apenas por aqueles que sabem
 reconhecê-las, construí-las e partilhá-las.
 </P>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link
 to="/contact?subject=Investir%20com%20NANDA%20S%C3%81"
 className="btn-arrow inline-flex items-center gap-3 bg-nanda-ink text-white px-7 py-4 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
 >
 Investir com a NANDA SÁ
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link
 to="/secteurs"
 className="btn-arrow inline-flex items-center gap-3 bg-transparent border border-nanda-ink text-nanda-ink px-7 py-4 hover:bg-nanda-ink hover:text-white transition-colors duration-500 label-overline"
 >
 Explorar os nossos sectores
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </>
);

/* ------------------------------------------------------------------ */
/* Axes stratégiques transfrontaliers — Guinée-Bissau & Complémentarités */
/* ------------------------------------------------------------------ */
const StrategicAxes = () => {
 const tt = useT();
 return (
 <>
 {/* RÉGIONS DE LA GUINÉE-BISSAU */}
 <Section testId="opp-regions-detail" className="bg-nanda-bgAlt">
 <FadeUp>
 <div className="label-overline text-nanda-terracotta mb-6">
 {tt("Les régions", "The regions", "As regiões")}
 </div>
 <H2>
 {tt(
 "Les régions et opportunités de la Guinée-Bissau.",
 "The regions and opportunities of Guinea-Bissau.",
 "As regiões e oportunidades da Guiné-Bissau."
 )}
 </H2>
 <P>
 {tt(
 "De Cacheu à Bafatá, de Bissau aux Bijagós, la Guinée-Bissau se compose de neuf régions administratives aux profils économiques distincts. Chacune apporte ses filières, ses savoir-faire, ses paysages et ses opportunités. Ce maillage territorial est l'un des atouts les moins connus du pays — et l'un des plus déterminants pour un investisseur qui prend le temps de le lire.",
 "From Cacheu to Bafatá, from Bissau to the Bijagós, Guinea-Bissau is made up of nine administrative regions with distinct economic profiles. Each brings its value chains, its know-how, its landscapes and its opportunities. This territorial mesh is one of the country's least-known assets — and one of the most decisive for investors who take the time to read it.",
 "De Cacheu a Bafatá, de Bissau aos Bijagós, a Guiné-Bissau compõe-se de nove regiões administrativas com perfis económicos distintos. Cada uma traz as suas fileiras, os seus saberes, as suas paisagens e as suas oportunidades. Esta malha territorial é um dos activos menos conhecidos do país — e um dos mais determinantes para investidores que dedicam tempo a lê-la."
 )}
 </P>
 <H3>{tt("Filières phares", "Flagship value chains", "Fileiras emblemáticas")}</H3>
 <UL
 items={[
 tt("Anacarde de Cacheu et de Bafatá — principaux bassins de production nationale", "Cashew from Cacheu and Bafatá — main national production basins", "Caju de Cacheu e Bafatá — principais bacias de produção nacional"),
 tt("Fruits tropicaux, mangue, agrumes, madd, banane, sur les axes Cacheu–Canchungo–Bafatá", "Tropical fruits, mango, citrus, madd, banana, along the Cacheu–Canchungo–Bafatá axes", "Frutos tropicais, manga, citrinos, madd, banana, nos eixos Cacheu–Canchungo–Bafatá"),
 tt("Riz de mangrove et riziculture pluviale (traditions Balante, Manjak, Papel)", "Mangrove and rainfed rice growing (Balanta, Manjak, Papel traditions)", "Arroz de mangal e arroz de sequeiro (tradições Balanta, Manjaco, Papel)"),
 tt("Miel de forêt, huile de palme rouge, sel de terre, fleur d'hibiscus", "Forest honey, red palm oil, earth salt, hibiscus flower", "Mel da floresta, óleo de palma vermelho, sal da terra, flor de hibisco"),
 tt("Tourisme d'expérience : Bijagós (Bubaque, Orango, Rubane) et plages de Varela", "Experiential tourism: Bijagós archipelago (Bubaque, Orango, Rubane) and Varela beaches", "Turismo de experiência: Bijagós (Bubaque, Orango, Rubane) e praias de Varela"),
 tt("Pêche artisanale et transformation halieutique (bolongs et estuaires bissau-guinéens)", "Artisanal fishing and seafood processing (Bissau-Guinean bolongs and estuaries)", "Pesca artesanal e transformação piscícola (bolongs e estuários bissau-guineenses)"),
 ]}
 />
 <H3>{tt("Un cadre sécurisant", "A secure framework", "Um enquadramento seguro")}</H3>
 <P>
 {tt(
 "La Guinée-Bissau bénéficie d'un cadre juridique bissau-guinéen consolidé, de la stabilité de la zone Franc CFA, d'un accès facilité aux marchés de l'UEMOA et de la CEDEAO. Elle est desservie par l'aéroport international Osvaldo Vieira à Bissau et le port autonome de Bissau, principale porte océanique du pays.",
 "Guinea-Bissau benefits from a consolidated Bissau-Guinean legal framework, from the stability of the CFA Franc zone and from facilitated access to WAEMU and ECOWAS markets. It is served by the Osvaldo Vieira International Airport in Bissau and the autonomous port of Bissau, the country's main ocean gateway.",
 "A Guiné-Bissau beneficia de um quadro jurídico bissau-guineense consolidado, da estabilidade da zona Franco CFA e do acesso facilitado aos mercados da UEMOA e da CEDEAO. É servida pelo Aeroporto Internacional Osvaldo Vieira em Bissau e pelo porto autónomo de Bissau, principal porta oceânica do país."
 )}
 </P>
 </FadeUp>
 <NandaSaFoodCTA />
 </Section>

 {/* COMPLÉMENTARITÉS */}
 <Section testId="opp-complementarites">
 <FadeUp>
 <div className="label-overline text-nanda-terracotta mb-6">
 {tt("Régions et complémentarités", "Regions and complementarities", "Regiões e complementaridades")}
 </div>
 <H2>
 {tt(
 "Les complémentarités entre régions bissau-guinéennes.",
 "The complementarities between Bissau-Guinean regions.",
 "As complementaridades entre regiões bissau-guineenses."
 )}
 </H2>
 <P>
 {tt(
 "Les régions de Cacheu, Oio et Bafatá forment un même bassin humain, agricole et commercial. NANDA SÁ inscrit son action dans cette réalité territoriale, en organisant les flux de valeur entre ces régions bissau-guinéennes.",
 "Cacheu, Oio and Bafatá form the same human, agricultural and commercial basin. NANDA SÁ works within this territorial reality by orchestrating value flows between these Bissau-Guinean regions.",
 "Cacheu, Oio e Bafatá formam a mesma bacia humana, agrícola e comercial. A NANDA SÁ inscreve a sua acção nesta realidade territorial, organizando os fluxos de valor entre estas regiões bissau-guineenses."
 )}
 </P>
 <H3>{tt("Ce qui circule déjà", "What already flows", "O que já circula")}</H3>
 <UL
 items={[
 tt("Anacarde brute — de Cacheu vers Canchungo pour transformation partielle", "Raw cashew — from Cacheu to Canchungo for partial processing", "Caju em bruto — de Cacheu para Canchungo para transformação parcial"),
 tt("Riz, fruits, sel, huile de palme — flux journaliers dans les deux sens", "Rice, fruit, salt, palm oil — daily flows in both directions", "Arroz, frutos, sal, óleo de palma — fluxos diários nos dois sentidos"),
 tt("Main-d'œuvre agricole saisonnière — récoltes coordonnées de part et d'autre", "Seasonal farm labour — harvests coordinated on both sides", "Mão-de-obra agrícola sazonal — colheitas coordenadas nos dois lados"),
 tt("Langues et familles étendues — un même écosystème humain", "Languages and extended families — a shared human ecosystem", "Línguas e famílias alargadas — um mesmo ecossistema humano"),
 tt("Diaspora — les régions de la Guinée-Bissau nourrissent les mêmes réseaux vers l'Europe et les Amériques", "Diaspora — Guinea-Bissau feeds the same networks toward Europe and the Americas", "Diáspora — a Guiné-Bissau alimenta as mesmas redes para a Europa e as Américas"),
 ]}
 />
 <H3>{tt("Ce que NANDA SÁ orchestre", "What NANDA SÁ orchestrates", "O que a NANDA SÁ orquestra")}</H3>
 <P>
 {tt(
 "Nous structurons des filières régionales : sourcing dans les zones les plus productives, transformation là où elle est la plus efficiente (Canchungo ou Bafatá), export via le port qui offre le meilleur coût logistique. C'est ce modèle territorial qui explique la solidité de nos Maisons NANDA SÁ et NANDA SÁ Cosmétiques.",
 "We structure regional value chains: sourcing in the most productive zones, processing where it is most efficient (Canchungo or Bafatá), export via the port that offers the best logistics cost. This territorial model explains the resilience of our NANDA SÁ and NANDA SÁ Cosmétiques Houses.",
 "Estruturamos fileiras regionais: sourcing nas zonas mais produtivas, transformação onde é mais eficiente (Canchungo ou Bafatá), exportação pelo porto de melhor custo logístico. É este modelo territorial que explica a solidez das nossas Casas NANDA SÁ e NANDA SÁ Cosmétiques."
 )}
 </P>
 </FadeUp>
 <EleoneCTA />
 </Section>

 {/* INFRASTRUCTURES */}
 <Section testId="opp-infrastructures" className="bg-nanda-bgAlt">
 <FadeUp>
 <div className="label-overline text-nanda-terracotta mb-6">
 {tt("Infrastructures", "Infrastructure", "Infra-estruturas")}
 </div>
 <H2>
 {tt(
 "Un maillage qui se consolide sur l'ensemble du territoire.",
 "A network being consolidated across the country.",
 "Uma malha que se consolida em todo o país."
 )}
 </H2>
 <H3>{tt("Ports & maritime", "Ports & maritime", "Portos e via marítima")}</H3>
 <UL
 items={[
 tt("Port de Bissau — principale porte océanique de la Guinée-Bissau, modernisation en cours", "Port of Bissau — Guinea-Bissau's main ocean gateway, currently being modernised", "Porto de Bissau — principal porta oceânica da Guiné-Bissau, em modernização"),
 tt("Port de Cacheu — connecté à Bissau par voie maritime et fluviale", "Port of Cacheu — connected to Bissau by sea and river", "Porto de Cacheu — ligado a Bissau por via marítima e fluvial"),
 tt("Port autonome de Bissau — hub régional CEDEAO, débouché naturel des filières transformées", "Port of Bissau — regional ECOWAS hub, natural outlet for processed value chains", "Porto autónomo de Bissau — polo regional CEDEAO, saída natural das fileiras transformadas"),
 ]}
 />
 <H3>{tt("Routier & corridors", "Roads & corridors", "Rodoviário e corredores")}</H3>
 <UL
 items={[
 tt("Corridor Bissau–São Domingos–Cacheu–Bafatá : moins de 8 h de route en conditions normales", "Bissau–São Domingos–Bafatá–Bissau corridor: less than 8 h by road in normal conditions", "Corredor Bissau–São Domingos–Cacheu–Bafatá: menos de 8 h de estrada em condições normais"),
 tt("Route Canchungo–Cacheu–São Domingos, réhabilitée et sécurisée", "Canchungo–Cacheu–São Domingos road, rehabilitated and secured", "Estrada Canchungo–Cacheu–São Domingos, reabilitada e segura"),
 tt("Poste frontière moderne à São Domingos–Mpack, dédouanement CEDEAO facilité", "Modern border post at São Domingos–Mpack, streamlined ECOWAS clearance", "Posto fronteiriço moderno em São Domingos–Mpack, desalfandegamento CEDEAO facilitado"),
 ]}
 />
 <H3>{tt("Aérien & énergie", "Air & energy", "Aéreo e energia")}</H3>
 <UL
 items={[
 tt("Aéroport international Osvaldo Vieira (Bissau) et aéroport régional de Bafatá", "Osvaldo Vieira International Airport (Bissau) and Bafatá Regional Airport", "Aeroporto Internacional Osvaldo Vieira (Bissau) e Aeroporto Regional de Bafatá"),
 tt("Interconnexion électrique régionale OMVG (Organisation pour la Mise en Valeur du fleuve Gambie) en déploiement", "OMVG regional electrical interconnection (Gambia River Basin Organisation) being deployed", "Interligação eléctrica regional OMVG (Organização para o Aproveitamento do Rio Gâmbia) em implementação"),
 tt("Fort potentiel solaire (2 500 à 3 000 h d'ensoleillement/an) — solutions déployées par NANDA SÁ Energia Pro", "Strong solar potential (2,500 to 3,000 h of sunshine per year) — solutions deployed by NANDA SÁ Energia Pro", "Forte potencial solar (2 500 a 3 000 h de sol por ano) — soluções implementadas pela NANDA SÁ Energia Pro"),
 ]}
 />
 </FadeUp>
 <EnergiaProCTA />
 </Section>

 {/* GRANDS PROJETS */}
 <Section testId="opp-grands-projets">
 <FadeUp>
 <div className="label-overline text-nanda-terracotta mb-6">
 {tt("Grands projets", "Major projects", "Grandes projectos")}
 </div>
 <H2>
 {tt(
 "Les chantiers qui redessinent le territoire.",
 "The projects reshaping the territory.",
 "Os estaleiros que redesenham o território."
 )}
 </H2>
 <P>
 {tt(
 "Plusieurs initiatives publiques et privées structurent l'espace transfrontalier. Les investisseurs qui se positionnent aujourd'hui construisent une valeur qui accompagnera ces chantiers.",
 "Several public and private initiatives are structuring the cross-border space. Investors positioning themselves today are building value that will grow with these projects.",
 "Várias iniciativas públicas e privadas estão a estruturar o espaço transfronteiriço. Os investidores que se posicionam hoje constroem valor que acompanhará estes projectos."
 )}
 </P>
 <UL
 items={[
 tt("Interconnexion OMVG — un réseau électrique unifié Guinée-Bissau · Gambie · Guinée", "OMVG interconnection — a unified electricity grid Guinea-Bissau · Gambia · Guinea", "Interligação OMVG — uma rede eléctrica unificada Guiné-Bissau · Gâmbia · Guiné"),
 tt("Modernisation du port autonome de Bissau — capacité doublée à l'horizon 2030", "Modernisation of the autonomous port of Bissau — capacity doubled by 2030", "Modernização do porto autónomo de Bissau — capacidade duplicada até 2030"),
 tt("Corridor Bissau–Bamako revisité — impact indirect sur les flux logistiques du Sud", "Revisited Bissau–Bamako corridor — indirect impact on Southern logistics flows", "Corredor Bissau–Bamako revisitado — impacto indirecto nos fluxos logísticos do Sul"),
 tt("Zones économiques spéciales (ZES) — projets d'implantation en Guinée-Bissau", "Special Economic Zones (SEZ) — establishment projects in Guinea-Bissau", "Zonas Económicas Especiais (ZEE) — projectos de implantação na Guiné-Bissau"),
 tt("Pôle de transformation cajou de Canchungo — porté par NANDA SÁ et ses partenaires coopératifs", "Canchungo cashew processing hub — driven by NANDA SÁ and its cooperative partners", "Polo de transformação do caju de Canchungo — liderado pela NANDA SÁ e seus parceiros cooperativos"),
 tt("Corridor touristique Bijagós–Varela — projet de mise en réseau des acteurs", "Bijagós–Varela tourist corridor — project to network stakeholders", "Corredor turístico Bijagós–Varela — projecto de rede de actores"),
 ]}
 />
 </FadeUp>
 <ConseilCTA />
 </Section>
 </>
 );
};

/* ------------------------------------------------------------------ */
/* Formulaire de téléchargement — Lead capture MongoDB */
/* ------------------------------------------------------------------ */
const DownloadForm = () => {
 const tt = useT();
 return (
 <Section testId="opp-download-form" className="bg-nanda-sand/40">
 <FadeUp>
 <div className="label-overline text-nanda-terracotta mb-6">
 {tt("Dossier PDF gratuit", "Free PDF dossier", "Dossier PDF gratuito")}
 </div>
 <H2>
 {tt(
 "Recevez gratuitement votre dossier d'opportunités.",
 "Receive your opportunities dossier for free.",
 "Receba gratuitamente o seu dossiê de oportunidades."
 )}
 </H2>
 <P>
 {tt(
 "Un document de synthèse préparé par NANDA SÁ : opportunités de Canchungo, opportunités de Guinée-Bissau, chiffres clés de la filière cajou, secteurs à fort potentiel, conseils pour investir et premiers contacts utiles. Trilingue à venir (FR · EN · PT).",
 "A synthesis document prepared by NANDA SÁ: Canchungo opportunities, Guinée-Bissau opportunities, cashew value chain key figures, high-potential sectors, investment advice and first useful contacts. Trilingual coming soon (FR · EN · PT).",
 "Um documento de síntese preparado pela NANDA SÁ: oportunidades de Canchungo, oportunidades da Guiné-Bissau, dados-chave da fileira do caju, sectores de forte potencial, conselhos para investir e primeiros contactos úteis. Trilingue em breve (FR · EN · PT)."
 )}
 </P>
 <div className="mt-10">
 <LeadCaptureForm
 pdfUrl={PDF_URL}
 pdfLabel={tt("Télécharger le dossier PDF", "Download the PDF dossier", "Transferir o dossiê PDF")}
 interest="opportunites-guinee-bissau"
 source="opportunites-page"
 />
 </div>
 </FadeUp>
 </Section>
 );
};

/* ------------------------------------------------------------------ */
/* Page principale */
/* ------------------------------------------------------------------ */
export default function Opportunites() {
 const { locale } = useLanguage();
 const tt = useT();

 const title = tt(
 "Investir en Guinée-Bissau — Opportunités, secteurs et données | NANDA SÁ",
 "Invest in Guinea-Bissau — Opportunities, sectors & data | NANDA SÁ",
 "Investir na Guiné-Bissau — Oportunidades, sectores e dados | NANDA SÁ"
 );
 const description = tt(
 "Le panorama de référence sur les opportunités économiques de la Guinée-Bissau : cajou, agriculture, énergie solaire, tourisme, immobilier, logistique, infrastructures, grands projets. Par NANDA SÁ, passerelle économique transfrontalière.",
 "The reference panorama on economic opportunities in Guinea-Bissau: cashew, agriculture, solar energy, tourism, real estate, logistics, infrastructure, major projects. By NANDA SÁ, cross-border economic bridge.",
 "O panorama de referência sobre as oportunidades económicas da Guiné-Bissau e da Guiné-Bissau: caju, agricultura, energia solar, turismo, imobiliário, logística, infra-estruturas, grandes projectos. Pela NANDA SÁ, ponte económica ancorada no território nacional."
 );

 useArticleSeo(title, description, locale);

 return (
 <div data-testid="page-opportunites">
 {/* HERO */}
 <section className="relative h-[72vh] min-h-[520px] w-full overflow-hidden bg-nanda-ink">
 <img
 src={HERO}
 alt={tt(
 "Vue aérienne de Canchungo, chef-lieu de la région de Cacheu, Guinée-Bissau",
 "Aerial view of Canchungo, capital of the Cacheu region, Guinea-Bissau",
 "Vista aérea de Canchungo, capital da região de Cacheu, Guiné-Bissau"
 )}
 className="absolute inset-0 h-full w-full object-cover"
 loading="eager"
 />
 <div className="absolute inset-0 bg-black/45" />
 <div className="absolute inset-0 bg-gradient-to-r from-nanda-ink/70 via-nanda-ink/30 to-transparent" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-16 md:pb-24">
 <div className="label-overline text-white/80 mb-6">
 {tt("Panorama · Investir en Guinée-Bissau", "Panorama · Invest in Guinea-Bissau", "Panorama · Investir na Guiné-Bissau")}
 </div>
 <h1 className="font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-4xl">
 {tt("Les opportunités de la Guinée-Bissau.", "The opportunities of Guinea-Bissau.", "As oportunidades da Guiné-Bissau.")}
 </h1>
 <p className="mt-8 max-w-2xl text-white/90 text-lg md:text-xl leading-relaxed">
 {tt(
 "Un territoire d'avenir. Le panorama de référence pour investisseurs, entrepreneurs, institutions et diaspora explorant la Guinée-Bissau.",
 "A land of the future. The reference panorama for investors, entrepreneurs, institutions and diaspora exploring Guinea-Bissau.",
 "Um território de futuro, no cruzamento entre o Guiné-Bissau. O panorama de referência para investidores, empreendedores, instituições e diáspora."
 )}
 </p>
 </div>
 </section>

 {/* CONTENT */}
 {locale === "en" ? <ContentEN /> : locale === "pt" ? <ContentPT /> : <ContentFR />}

 {/* AXES STRATÉGIQUES TRANSFRONTALIERS */}
 <StrategicAxes />

 {/* CARTE INTERACTIVE */}
 <RegionalMap />

 {/* DOWNLOAD FORM — placé avant la conclusion visuelle en bas */}
 <DownloadForm />
 </div>
 );
}
